import api, { events, DOCUMENT_JOB_HEADERS } from '../api';
import { uploadThroughEngine, uploadEachThroughEngine } from './documents.engine';
import { convertHeicPhotos } from './photo.heic.conversion';
import axios from 'axios';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import Swal from 'sweetalert2';


import stepsSolicitation from '../../constant/steps.solicitation.constant';
import { areas } from '../../constant/area.constant';
import modalities from '../../constant/modalities.constant';

import { withClientMark, withFailureMark, isRetryableStatus } from './documents.link';
import loginService from '../login.service';
import serviceWorker from '../service.worker.service';
import clientLogger from '../logging/client.logger';

const fileDownload = require('js-file-download');


const isIosWebkit = () => {
    const os = serviceWorker.getOS();
    return os === 'ios' || (os === 'macos' && navigator.maxTouchPoints > 1);
};

const find = async (requestId) =>
    api.get({ type: 'user', service: `requests/${requestId}/documents` });

const findServiceDocuments = async (requestId) =>
    api.get({ type: 'user', service: `requests/${requestId}/documents/service` });

const findServiceDocumentBySenderUserId = async (requestId, serviceId, userId) =>
    api.get({ type: 'user', service: `requests/${requestId}/documents/service/${serviceId}/user/${userId}` });

const transcribeAudioDocument = async (requestId, documentId) =>
    api.post({ type: 'user', service: `requests/${requestId}/documents/${documentId}/audio-transcription/` })
        .then(res => res.data);

const checkUpload = async (requestId, payload, notifyOnError) =>
    api.post({
        type: 'user',
        service: `requests/${requestId}/documents/check-upload?notifyOnError=${notifyOnError}`,
        data: { payload },
    });

const createStaging = async (requestId, files) =>
    api.post({
        type: 'user',
        service: `requests/${requestId}/documents/staging`,
        data: files.map(file => ({
            name: file.name,
            fileType: file.type,
            fileSize: file.size,
        })),
    }).then(res => res && res.data);

const findStaging = async (requestId) =>
    api.get({
        type: 'user',
        service: `requests/${requestId}/documents/staging`,
    }).then(response => (response && response.data) || []);

const toClassifiedItem = (staging) => ({
    stagingId: staging.stagingId,
    name: staging.name,
    suggestedName: staging.classification && staging.classification.suggestedName,
    classification: staging.classification,
    classificationStatus: staging.classificationStatus,
});

const waitForClassification = async (requestId, jobId, stagingIds) => {
    if (!await api.followJob(jobId))
        throw new Error('A classificação dos documentos está demorando mais que o normal. Tente novamente em instantes.');

    const wanted = new Set((stagingIds || []).map(Number));
    const stagings = (await findStaging(requestId)).filter(staging => !wanted.size || wanted.has(Number(staging.stagingId)));

    return { items: stagings.map(toClassifiedItem) };
};

const classifyStaging = async (requestId, stagingIds = null) => {
    const requested = await api.post({
        type: 'user',
        service: `requests/${requestId}/documents/staging/classify`,
        data: stagingIds && stagingIds.length > 0 ? { stagingIds } : {},
        headers: DOCUMENT_JOB_HEADERS,
    }).then(res => res && res.data);

    if (!requested || !requested.jobId)
        return requested;

    return waitForClassification(requestId, requested.jobId, requested.stagingIds || stagingIds);
};

const finalizeStaging = async (requestId, items) =>
    api.post({
        type: 'user',
        service: `requests/${requestId}/documents/staging/finalize`,
        data: items,
    }).then(res => res && res.data);

const removeStaging = async (requestId, stagingId) =>
    api.remove({
        type: 'user',
        service: `requests/${requestId}/documents/staging/${stagingId}`,
    });

const saveAllStaging = async (requestId, selectedFiles, onProgress = null) => {

    if (!selectedFiles || selectedFiles.length === 0) return [];

    const files = await convertHeicPhotos(selectedFiles);
    const reportProgress = typeof onProgress === 'function'
        ? (file, percent) => onProgress(selectedFiles[files.indexOf(file)], percent)
        : null;

    const outcomes = await uploadThroughEngine({ files, targetKind: 'requestStaging', targetOf: () => ({ requestId }), showProgress: false, onProgress: reportProgress });
    if (outcomes) return outcomes.map(outcome => outcome.result.staging);

    const stagings = await createStaging(requestId, files);
    if (!stagings || stagings.length !== files.length)
        throw new Error('Resposta de staging incompleta.');

    await Promise.all(
        stagings.map((staging, idx) => {
            const file = files[idx];
            const signedUrl = staging.signedRequest && staging.signedRequest.url
                ? staging.signedRequest.url
                : staging.signedRequest;
            const itemOnProgress = reportProgress
                ? (percent) => reportProgress(file, percent)
                : null;
            return uploadFile(file, signedUrl, { document: { name: file.name } }, false, itemOnProgress);
        }),
    );

    return stagings;
};

const engineTargetOf = (requestId) => (file) => {
    const { name, fileSize, fileType, ...target } = getDocument(file);
    return { ...target, requestId };
};

const saveAll = async (requestId, selectedFiles, showProgress = true) => {

    if (!selectedFiles || selectedFiles.length === 0) return;

    const files = await convertHeicPhotos(selectedFiles, { showProgress });

    const outcomes = await uploadThroughEngine({ files, targetKind: 'requestDocument', targetOf: engineTargetOf(requestId), showProgress });
    if (outcomes) return outcomes.map(outcome => ({ document: outcome.result.document, success: true }));

    const documents = await getSignedRequest(requestId, files);

    return await requestFiles(files, documents || [], showProgress);
};

const settleWithoutEngine = async (requestId, files, originalOf, onFileSettled) => {
    const settledAll = (settledOf) => files.map(file => {
        const settled = settledOf(file);
        onFileSettled(originalOf(file), settled);
        return settled;
    });
    try {
        const documents = await getSignedRequest(requestId, files);
        await requestFiles(files, documents || [], false);
        const documentOf = (file) => ((documents || []).find(item => item.document && item.document.name === file.name) || {}).document;
        return settledAll(file => ({ status: 'completed', document: documentOf(file) }));
    } catch (error) {
        clientLogger.logClientError({ context: 'request:documents:upload', message: 'envio sem o motor falhou', payload: { requestId }, error });
        const message = (error && error.response && error.response.data && error.response.data.message) || 'Não foi possível enviar este arquivo.';
        return settledAll(() => ({ status: 'failed', message }));
    }
};

const saveEach = async (requestId, selectedFiles, { onFileState = () => {}, onFileSettled = () => {} } = {}) => {

    if (!selectedFiles || selectedFiles.length === 0) return [];

    const files = await convertHeicPhotos(selectedFiles, { showProgress: false });
    const originalOf = (file) => selectedFiles[files.indexOf(file)] || file;

    const outcomes = await uploadEachThroughEngine({
        files,
        targetKind: 'requestDocument',
        targetOf: engineTargetOf(requestId),
        onFileState: (file, state) => onFileState(originalOf(file), state),
        onFileSettled: (file, settled) => onFileSettled(originalOf(file), settled),
    });
    if (outcomes) return outcomes;

    return settleWithoutEngine(requestId, files, originalOf, onFileSettled);
};

const getSignedRequest = async (requestId, files) => {

    const data = files.map(item => getDocument(item));

    return await api
        .post({ type: 'user', service: `requests/${requestId}/documents`, data })
        .then(({ data }) => data);
}

const update = async (document) =>
    api.put({
        type: 'user',
        service: `requests/${document.requestId}/documents/${document.id}`,
        data: document,
    });

const remove = async (document) =>
    api.remove({
        type: 'user',
        service: `requests/${document.requestId}/documents/${document.id}`,
    });

const getDocument = (item) => ({
    name: item.name,
    fileSize: item.size,
    fileType: item.type,
    nameType: item.nameType,
    forCorrection: item.forCorrection,
    serviceId: item.serviceId,
    isVisibleCustomer: item.isVisibleCustomer,
    isServiceMainDocument: item.isServiceMainDocument,
    fieldId: item?.fieldId,
    isProcessMainDocument: item?.isProcessMainDocument,
    source: item?.source,
    additionalInformation: item?.additionalInformation
});

const uploadFile = (file, signedRequest, item, showProgress = true, onProgress = null) =>
    new Promise((resolve, reject) => {
        try {
            const xhr = new XMLHttpRequest();
            xhr.open('PUT', signedRequest);

            xhr.upload.onprogress = (e) => {
                const progressData = {
                    total: e.total,
                    loaded: e.loaded,
                    filename: item.document.name,
                    type: 'Upload',
                };

                setProgress(progressData, showProgress);

                if (typeof onProgress === 'function' && e.lengthComputable && e.total > 0) {
                    onProgress(Math.round((e.loaded / e.total) * 100));
                }
            }

            xhr.onloadend = () =>
                xhr.status >= 200 && xhr.status < 300
                    ? resolve({ ...item, success: true })
                    : reject({ ...item, success: false, status: xhr.status });

            xhr.onerror = () => reject({ ...item, success: false });

            xhr.send(file);
        } catch (error) {
            reject({ ...item, success: false });
        }
    });

const openInNewTab = (url) => {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.target = '_blank';
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
};

// O link do documento carrega a assinatura de acesso na query; o log fica só com o caminho.
const withoutQuery = (url) => String(url || '').split('?')[0];

const notifyDownloadFailure = (url, filename, error) => {
    clientLogger.logClientError({
        context: 'documents:download',
        message: 'Download do documento falhou no navegador',
        payload: {
            documentUrl: withoutQuery(url),
            filename,
            status: error?.response?.status ?? null,
            code: error?.code ?? null,
        },
        error,
    });

    return Swal.fire({
        icon: 'error',
        title: 'Não foi possível baixar o arquivo',
        text: `O download de "${filename}" não foi concluído. Isso costuma acontecer quando o navegador, um antivírus ou a rede bloqueia o download. Clique em "Abrir em nova aba" para baixar direto. Se continuar, fale com o nosso atendimento.`,
        confirmButtonText: 'Abrir em nova aba',
        cancelButtonText: 'Fechar',
        showCancelButton: true,
    }).then((result) => result.isConfirmed && openInNewTab(url));
};

const notifyPartialZip = (missing, total) => {
    clientLogger.logClientError({
        context: 'documents:zip:partial',
        message: 'Download em ZIP saiu sem parte dos arquivos',
        payload: { missing, total },
    });

    return Swal.fire({
        icon: 'warning',
        title: 'Alguns arquivos ficaram fora do ZIP',
        text: `Não foi possível baixar ${missing.length} de ${total} arquivos: ${missing.join(', ')}. Baixe esses arquivos um a um.`,
        confirmButtonText: 'Entendi',
    });
};

const customerLink = (url) => withClientMark(url, loginService.isCustomer());

const fetchBlob = (url, filename) =>
    axios({
        method: 'get',
        url,
        responseType: 'blob',
        onDownloadProgress: (progressEvent) =>
            setProgress({
                total: progressEvent.total,
                loaded: progressEvent.loaded,
                filename,
                type: 'Download',
            }),
    });

const fetchWithFallback = (url, filename) =>
    fetchBlob(url, filename).catch((error) =>
        isRetryableStatus(error?.response?.status) ? fetchBlob(withFailureMark(url), filename) : Promise.reject(error),
    );

const fetchOk = (url) => fetch(url).then((response) => (response.ok ? response : Promise.reject(response)));

const fetchZipEntry = (url) =>
    fetchOk(url).catch((failure) => (isRetryableStatus(failure?.status) ? fetchOk(withFailureMark(url)) : Promise.reject(failure)));

const downloadFile = async (url, filename) => {
    const link = customerLink(url);

    if (isIosWebkit()) {
        openInNewTab(link);
        return;
    }

    await fetchWithFallback(link, filename)
        .then((res) => fileDownload(res.data, filename))
        .catch((error) => notifyDownloadFailure(link, filename, error))
        .finally(() =>
            events.emmit('onProgressDeterminate', {
                info: 'Download concluído',
                progress: 100,
                show: false,
            }),
        );
};

const downloadAllAsZip = async (selectedDocuments, zipName) => {
    const total = selectedDocuments.length;
    let downloaded = 0;
    const missing = [];

    events.emmit('onProgressDeterminate', {
        info: `Baixando documentos (0/${total})`,
        progress: 0,
        show: true,
    });

    const zip = new JSZip();

    const addFileToZip = async (document) => {
        const blob = await fetchZipEntry(customerLink(document.url)).then((response) => response.blob()).catch(() => null);
        if (blob) zip.file(document.name, blob);
        else missing.push(document.name);

        downloaded += 1;
        events.emmit('onProgressDeterminate', {
            info: `Baixando documentos (${downloaded}/${total})`,
            progress: Math.round((downloaded / total) * 85),
            show: true,
        });
    };

    try {
        await Promise.all(selectedDocuments.map(addFileToZip));
        if (total > 0 && missing.length === total) throw new Error('documents:zip:empty');

        const content = await zip.generateAsync({ type: 'blob', compression: 'STORE' }, (metadata) =>
            events.emmit('onProgressDeterminate', {
                info: 'Compactando arquivos...',
                progress: 85 + Math.round((metadata.percent / 100) * 15),
                show: true,
            }),
        );

        saveAs(content, zipName);
        events.emmit('onProgressDeterminate', { info: 'Download concluído', progress: 100, show: false });
        if (missing.length) notifyPartialZip(missing, total);
    } catch (error) {
        events.emmit('onProgressDeterminate', { info: 'Falha no download', progress: 0, show: false });
        throw error;
    }
};

const setProgress = (progressData, showProgress = true) => {
    if (!progressData.loaded) return;

    let progress = Math.round((progressData.loaded / progressData.total) * 100);
    events.emmit('onProgressDeterminate', {
        info: `Fazendo ${progressData.type} do arquivo: ${progressData.filename}`,
        progress,
        show: showProgress,
    });
};

const requestFiles = async (files, data, showProgress = true) => {

    events.emmit('onProgressDeterminate', {
        info: 'Verificando os arquivos',
        progress: 0,
        show: showProgress,
    });

    const reponseFiles = await Promise.all(
        data.map((item) => {
            if (!item.document) return;

            let file = files.find((x) => x.name == item.document.name);

            return uploadFile(file, item.signedRequest, item, showProgress).catch((error) => fileError(error));
        }),
    );

    events.emmit('onProgressDeterminate', {
        info: 'Upload concluído com sucesso',
        progress: 100,
        show: false,
    });

    return reponseFiles;
};

const fileError = async (error) => {
    await remove(error.document);
    events.emmit('onProgressDeterminate', { info: 'Falha ao carregar o arquivo', progress: 0, show: false });
    throw error;
}

const uploadListByAreaId = (areaId) => {
    const list = {
        [areas.previdenciario.id]: [
            {
                nameType: 'cnis',
                title: 'Extrato do CNIS',
                required: true,
                modalityIds: [modalities.initial.id]
            },
            {
                nameType: 'ctps',
                title: 'Cópia da CTPS',
                required: true,
                modalityIds: [modalities.initial.id]
            },
            {
                nameType: 'administrativeProcess',
                title: 'Processo Administrativo',
                required: true,
                modalityIds: [modalities.initial.id]
            },
            {
                nameType: 'medicalDocuments',
                title: 'Documentos Médicos',
                required: false,
                modalityIds: [modalities.initial.id]
            },
            {
                nameType: 'witnessStatement',
                title: 'Declaração de Testemunhas',
                required: false,
                modalityIds: [modalities.initial.id]
            },
            {
                nameType: 'stableUnion',
                title: 'Documentos que comprovem união estável ou condição de dependente econômico',
                required: false,
                modalityIds: [modalities.initial.id]
            },
            {
                nameType: 'activitySpecialties',
                title: 'Cópia de PPP ou laudos que comprovem especialidade das atividades desempenhadas',
                required: false,
                modalityIds: [modalities.initial.id]
            }
        ]
    }

    return list[areaId] || []
}

const hasPending = (request, documents = []) => {

    if (request.modality != 'Inicial')
        return false;

    const requiredNameTypes = (uploadListByAreaId(request.areaId) || []).filter(item => item.required).map(item => item.nameType);
    return !requiredNameTypes.every(nameType => documents.some(doc => doc.nameType === nameType));
}

const getMissingDocs = (request, documents = []) => {
    const requiredDocs = (uploadListByAreaId(request.areaId) || []).filter(item => item.required)

    const missing = requiredDocs.filter(requiredDoc => !documents.some(doc => doc.nameType === requiredDoc.nameType))

    return missing.map(item => item.title)
}

const isDocumentRequired = (request, document) => {
    const requiredDocs = uploadListByAreaId(request.areaId).filter(item => item.required)

    return requiredDocs.map(item => item.nameType).includes(document.nameType)
}

const isAllowEnableToCustomer = (req) => {

    const user = loginService.getUserAuthentication();
    const isFaciliterReviewer = (req?.faciliterReviewers || [])[0]?.userId == user.id;

    return loginService.isInternal() || isFaciliterReviewer
}

const showRequiredDocumentConfirmation = (request, doc) =>
    request.status != stepsSolicitation.PENDING
    && request.status != stepsSolicitation.CONCLUDED
    && isDocumentRequired(request, doc)

const showServiceMainDocumentConfirmation = (request, doc) =>
    request.status == stepsSolicitation.CONCLUDED && doc.isServiceMainDocument

const isInCorrection = (request) =>
    request.status == stepsSolicitation.CONCLUDED && (request.isInCorrection || request.status == stepsSolicitation.CORRECTION)

const findByRequestIdAndFieldId = async (requestId, fieldId) =>
    api.get({ type: 'user', service: `requests/${requestId}/documents/${fieldId}` })
        .then(res => res.data || {});

const tagInfoByKey = (key, item, request) => ({
    correction: {
        title: 'Correção',
        color: 'orange',
        message: 'Documento de correção'
    },
    underCorrection: {
        title: 'Em correção',
        color: '#FB923C',
        message: 'Documento principal aguardando substituição pela nova versão'
    },
    versionCurrent: {
        title: 'Versão atual',
        color: '#009700',
        message: 'Versão mais recente do documento'
    },
    versionPrevious: {
        title: 'Versão anterior',
        color: 'gray',
        message: 'Versão arquivada após uma correção'
    },
    versionOriginal: {
        title: 'Entrega original',
        color: 'gray',
        message: 'Primeira versão entregue antes da correção'
    },
    serviceMainDocument: {
        title: item.serviceName,
        color: 'blue',
        message: `Documento principal do serviço de ${item.serviceName}`
    },
    finalVersion: {
        title: 'Versão final',
        color: 'green',
        message: request.status == 'CONCLUDED' ?
            'Esse documento está sendo mostrado para o cliente'
            : 'Esse documento será mostrado ao cliente quando a solicitação for concluída'
    },
    wasDevelopedWithIa: {
        title: 'Revisar e Incluir Jurisprudências',
        color: 'purple',
        message: 'Petição desenvolvida sem a inclusão de jurisprudências'
    },
    internalUpload: {
        title: 'Interno',
        color: 'blue',
        message: 'Esse documento foi anexado por um membro da Facilita Jurídico'
    },
    isVisibleCustomer: {
        title: 'Visível para o cliente',
        color: 'green',
        message: 'Esse arquivo está visível para o cliente na aba de anexos'
    },
    isProcessMainDocument: {
        title: 'Processo',
        color: 'blue',
        message: 'Esse é o documento principal onde contém todos os dados do processo'
    },
    isIAPetitionReview: {
        title: 'Revisada com IA',
        color: 'gray',
        message: 'Essa petição foi revisada com Inteligência Artificial após confecção'
    }
})[key]

const createDocumentInteractionRecord = async (requestId, payload) =>
    api.post({
        type: 'user',
        service: `requests/${requestId}/members/external/document/interaction-record`,
        data: payload,
    });

export default {
    find,
    checkUpload,
    saveAll,
    saveEach,
    saveAllStaging,
    classifyStaging,
    findStaging,
    finalizeStaging,
    removeStaging,
    update,
    remove,
    downloadFile,
    downloadAllAsZip,
    uploadFile,
    uploadListByAreaId,
    hasPending,
    getMissingDocs,
    isDocumentRequired,
    isAllowEnableToCustomer,
    findServiceDocuments,
    findServiceDocumentBySenderUserId,
    showRequiredDocumentConfirmation,
    showServiceMainDocumentConfirmation,
    isInCorrection,
    findByRequestIdAndFieldId,
    tagInfoByKey,
    createDocumentInteractionRecord,
    transcribeAudioDocument,
};
